import {
	ChevronDown,
	ExternalLink as ExternalLinkIcon,
	Link as LinkIcon,
	MapPin,
} from 'lucide-react'
import { useState } from 'react'
import { ExternalLink } from '@/shared/core/components/external-link/external-link.tsx'
import { useTranslation } from '@/shared/core/hooks/use-translation/use-translation.ts'
import {
	type ExperienceData,
	experiences,
} from '@/teams/portfolio/components/section/experience-section/experience-data.ts'
import { ExperienceTechTags } from '@/teams/portfolio/components/section/experience-section/experience-tech-tags/experience-tech-tags.tsx'
import '@/teams/portfolio/components/section/experience-section/experience-cards/experience-cards.scss'

export function ExperienceCards() {
	const [expandedId, setExpandedId] = useState<string | null>(
		experiences[0]?.id ?? null,
	)

	const handleToggle = (id: string) => {
		setExpandedId(expandedId === id ? null : id)
	}

	return (
		<div className='experience-cards' data-testid={'experience-cards'}>
			{experiences.map((experience, index) => {
				const isExpanded = expandedId === experience.id
				const isLast = index === experiences.length - 1

				return (
					<ExperienceCard
						experience={experience}
						index={index}
						isExpanded={isExpanded}
						isLast={isLast}
						isTimelineActive={true}
						key={experience.id}
						onToggle={() => handleToggle(experience.id)}
					/>
				)
			})}
		</div>
	)
}

interface ExperienceCardProps {
	experience: ExperienceData
	index: number
	isExpanded: boolean
	isLast: boolean
	isTimelineActive: boolean
	onToggle: () => void
}

function ExperienceCard({
	experience,
	isExpanded,
	isLast,
	isTimelineActive,
	onToggle,
}: Readonly<ExperienceCardProps>) {
	const { t } = useTranslation()

	// Determine card classes
	const cardClasses = [
		'experience-card',
		isExpanded && 'experience-card--expanded',
		isTimelineActive && 'experience-card--timeline-active',
	]
		.filter(Boolean)
		.join(' ')

	return (
		<div className={cardClasses} data-testid={'experience-card'}>
			{/* Timeline - hidden on mobile via CSS */}
			<div className='experience-card__timeline'>
				<span
					className={`experience-card__dot${isTimelineActive ? ' experience-card__dot--active' : ''}`}
				/>
				<span
					className={`experience-card__arm${isTimelineActive ? ' experience-card__arm--active' : ''}`}
				/>
				{!isLast && (
					<span
						className={`experience-card__connector${isTimelineActive ? ' experience-card__connector--active' : ''}`}
					/>
				)}
			</div>

			<div className='experience-card__content'>
				<button
					aria-expanded={isExpanded}
					className='experience-card__header'
					onClick={onToggle}
					type='button'>
					<div className='experience-card__header-main'>
						{/* Line 1: Role + Company + Badge */}
						<div className='experience-card__title-row'>
							<h3 className='experience-card__role'>{t(experience.roleKey)}</h3>
							{/* biome-ignore lint/a11y/noNoninteractiveElementInteractions: wrapper to prevent click propagation to parent button */}
							{/* biome-ignore lint/a11y/noStaticElementInteractions: wrapper to prevent click propagation to parent button */}
							<span
								onClick={(e) => e.stopPropagation()}
								onKeyDown={(e) => e.stopPropagation()}>
								<ExternalLink
									className='experience-card__company'
									href='https://www.orange-business.com/'>
									@ {experience.company}
								</ExternalLink>
							</span>
							{experience.badgeKey && (
								<span className='experience-card__badge'>
									{t(experience.badgeKey)}
								</span>
							)}
						</div>

						<div className='experience-card__meta-row'>
							<MapPin aria-hidden='true' size={14} />
							<span>{experience.location}</span>
							<span className='experience-card__separator'>·</span>
							<span className='experience-card__period'>
								{t(experience.periodKey)}
							</span>
						</div>
					</div>
					<ChevronDown
						className={`experience-card__chevron${isExpanded ? ' experience-card__chevron--rotated' : ''}`}
						size={20}
					/>
				</button>

				<div
					aria-hidden={!isExpanded}
					className={`experience-card__body${isExpanded ? ' experience-card__body--visible' : ''}`}>
					<div className='experience-card__body-inner'>
						<p className='experience-card__description'>
							{t(experience.descriptionKey)}
						</p>

						<ul className='experience-card__highlights'>
							{experience.highlightKeys.map((key) => (
								<li className='experience-card__highlight' key={key}>
									{t(key)}
								</li>
							))}
						</ul>

						<ExperienceTechTags tech={experience.tech} />

						<div className='experience-card__product-link'>
							<LinkIcon aria-hidden='true' size={14} />
							<ExternalLink
								className='experience-card__product-link-text'
								href={experience.externalLink}>
								{t(experience.projectKey)} -{' '}
								{t('portfolio.section.experience.externalLink')}{' '}
								<ExternalLinkIcon aria-hidden='true' size={14} />
							</ExternalLink>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}
